import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  Background, ConnectionMode, Controls, MarkerType, ReactFlow, ReactFlowProvider, SelectionMode, useReactFlow,
} from '@xyflow/react'
import type { Connection, Dimensions, Edge, EdgeChange, NodeChange } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { serversApi } from '../../../api'
import { getErrorMessage } from '../../../api/client'
import type { EdgeServer, NetworkNode, NetworkNodeKind } from '../../../types'
import { channelFromEdge } from '../../deviceSpec/pullMerge'
import NetNode from './NetNode'
import type { NetFlowNode } from './NetNode'
import { LinkForm, MultiForm, NodeForm, Overview } from './NetworkSidePanel'
import { autoLayout } from './netLayout'
import type { LiveDevice } from './netModel'
import { ADDABLE, asSide, commsOf, freeSpot, ipFrom, ipTable, kindInfo, newLink, newNode, nodeView, orphanComms } from './netModel'
import type { NodeMove, SyncStatus } from './netSync'
import { VIEWER_COLORS, useNetworkSync } from './netSync'

// 서버 상세 [네트워크] — 현장 네트워크 도면. IP 를 가진 실물(스위치·공유기·변환기·설비 …)과 배선·IP 대역은 EM 이 원본,
// LiveEdge 디바이스는 노드로 안 그리고 접속 IP·연결 상태를 Edge 에서 실시간으로 읽어 같은 IP 장비 위에 표시한다.
// 실시간 공동 편집 — 같은 도면을 연 사람끼리 변경·선택·끄는 위치가 바로 보인다 (netSync)

const STATUS_TEXT: Record<SyncStatus, string> = {
  connecting: '연결 중…',
  live: '실시간 · 저장됨',
  saving: '저장 중…',
  reconnecting: '연결이 끊겨 다시 연결하는 중…',
  error: '연결 오류',
}

const nodeTypes = { net: NetNode }

/** 고른 노드·연결 — 왼쪽 드래그 상자, Shift·Ctrl+클릭으로 여러 개 */
type Selection = { nodes: string[]; links: string[] }
const NONE: Selection = { nodes: [], links: [] }
const toggle = (list: string[], key: string, on: boolean) =>
  on ? (list.includes(key) ? list : [...list, key]) : list.filter(k => k !== key)

export default function NetworkTab({ server }: { server: EdgeServer }) {
  const serverId = server.id
  const devicesQuery = useQuery({
    queryKey: ['servers', serverId, 'devices'],
    queryFn: () => serversApi.getDevices(serverId).then(r => r.data),
    enabled: server.isOnline,
    retry: false,
  })
  // 디바이스 목록에는 접속 주소가 없어 채널 설정에서 IP·포트를 읽는다 (현장 규모 ≤20)
  const edgeDevices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data])
  const channelQueries = useQueries({
    queries: edgeDevices.map(d => ({
      queryKey: ['servers', serverId, 'devices', d.code, 'channel'],
      queryFn: () => serversApi.getDeviceChannel(serverId, d.code).then(r => r.data),
      retry: false,
      staleTime: 60_000,
    })),
  })
  const channelSig = channelQueries.map(q => q.dataUpdatedAt).join(',')
  const issues = server.deviceIssues
  const running = server.reportedMonitoringState === 'Running'
  const live = useMemo(() => {
    const map = new Map<string, LiveDevice>()
    edgeDevices.forEach((d, i) => {
      const ch = channelQueries[i]?.data
      const values = ch ? channelFromEdge(ch.channelInfoJson) : {}
      const fault = issues?.some(x => x.code === d.code)
      map.set(d.code, {
        code: d.code, name: d.name, protocol: d.protocol ?? '',
        ip: ipFrom(values.ip ?? values.serverUri ?? values.host ?? ''), port: values.port ?? '',
        state: !running || issues == null ? 'unknown' : fault ? 'fault' : 'ok',
      })
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- channelQueries 는 매 렌더 새 배열, channelSig 로 바뀜을 본다
  }, [edgeDevices, channelSig, issues, running])

  return (
    <ReactFlowProvider>
      <NetworkEditor key={serverId} server={server} live={live}
        edgeNote={!server.isOnline ? 'Edge 오프라인 상태입니다. 디바이스는 저장된 정보로 표시됩니다.'
          : devicesQuery.isError ? getErrorMessage(devicesQuery.error, 'Edge 디바이스를 받지 못했습니다.') : null} />
    </ReactFlowProvider>
  )
}

function NetworkEditor({ server, live, edgeNote }: {
  server: EdgeServer
  live: Map<string, LiveDevice>
  edgeNote: string | null
}) {
  // 도면에 Edge 서버 노드가 없으면 처음 들어올 때 하나 만든다 — 키를 서버로 정해 둘이 동시에 들어와도 하나만 생긴다.
  // 예전 도면의 디바이스 노드(v0.1.61~63)는 지운다 — 디바이스는 이제 같은 IP 장비 위에 표시
  const sync = useNetworkSync(server.id, (doc, api) => {
    if (!doc.nodes.some(n => n.kind === 'edge'))
      api.setDoc(d => ({ ...d, nodes: [newNode('edge', 0, 0, { key: `edge-${server.id}`, name: server.name, ip: ipFrom(server.ipAddress) }), ...d.nodes] }))
    if (doc.nodes.some(n => n.kind === 'device')) api.setDoc(d => {
      const gone = new Set(d.nodes.filter(n => n.kind === 'device').map(n => n.key))
      return { ...d, nodes: d.nodes.filter(n => !gone.has(n.key)), links: d.links.filter(l => !gone.has(l.from) && !gone.has(l.to)) }
    })
  })
  const { doc, setDoc } = sync
  const rf = useReactFlow()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [sel, setSel] = useState<Selection>(NONE)
  const selectNode = (key: string) => setSel({ nodes: [key], links: [] })
  const [dragPos, setDragPos] = useState<Record<string, { x: number; y: number }>>({})
  const [full, setFull] = useState(false)
  const [measured, setMeasured] = useState<Record<string, Dimensions>>({})
  const [addOpen, setAddOpen] = useState(false)
  const dark = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark'

  const rows = useMemo(() => ipTable(doc, live), [doc, live])
  const issueOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) if (r.issue) for (const n of r.nodes) m.set(n.key, r.issue)
    return m
  }, [rows])
  const orphans = useMemo(() => orphanComms(doc, live), [doc, live])

  // 다른 사람 — 잡은 노드 표시·끄는 중인 위치
  const others = sync.viewers.filter(v => v.connectionId !== sync.me)
  const editorsOf = (key: string) => others.filter(v => v.selected?.includes(key)).map(v => ({ name: v.name, color: VIEWER_COLORS[v.color % VIEWER_COLORS.length] }))
  const remotePos = new Map(sync.moves.map(m => [m.key, { x: m.x, y: m.y }]))

  // 내 선택을 다른 사람에게 알린다
  const selectedSig = sel.nodes.join(',')
  const selectRef = useRef(sync.select)
  useEffect(() => { selectRef.current = sync.select })
  useEffect(() => { selectRef.current(selectedSig ? selectedSig.split(',') : []) }, [selectedSig, sync.ready])

  const nodes: NetFlowNode[] = doc.nodes.filter(n => n.kind !== 'device').map(n => {
    const v = nodeView(n, live)
    return {
      id: n.key,
      type: 'net',
      position: dragPos[n.key] ?? remotePos.get(n.key) ?? { x: n.x, y: n.y },
      measured: measured[n.key],
      selected: sel.nodes.includes(n.key),
      deletable: n.kind !== 'edge',
      data: { node: n, name: v.name, ip: v.ip, live: v.live, missing: v.missing, issue: issueOf.get(n.key) ?? null, editors: editorsOf(n.key), comms: commsOf(n, live) },
    }
  })
  const nameOf = (key: string) => {
    const n = doc.nodes.find(x => x.key === key)
    return n ? nodeView(n, live).name || kindInfo(n.kind).label : '?'
  }
  const edges: Edge[] = doc.links.map(l => {
    const ports = l.fromPort || l.toPort ? `${l.fromPort || '?'} ↔ ${l.toPort || '?'}` : ''
    const text = [ports, l.label].filter(Boolean).join(' · ')
    return {
      id: l.key, source: l.from, target: l.to,
      sourceHandle: asSide(l.fromSide, 'b'), targetHandle: asSide(l.toSide, 't'),
      selected: sel.links.includes(l.key),
      label: text || undefined,
      labelStyle: { fontSize: 10 },
      labelBgPadding: [4, 2] as [number, number],
      style: l.label.toUpperCase().startsWith('RS') ? { strokeDasharray: '5 3' } : undefined,
      markerEnd: undefined,
      type: 'smoothstep',
    }
  })

  const onNodesChange = (changes: NodeChange<NetFlowNode>[]) => {
    const removed: string[] = []
    const moving: Record<string, { x: number; y: number }> = {}
    const dropped: Record<string, { x: number; y: number }> = {}
    for (const c of changes) {
      if (c.type === 'dimensions' && c.dimensions) {
        const dim = c.dimensions
        setMeasured(m => (m[c.id]?.width === dim.width && m[c.id]?.height === dim.height ? m : { ...m, [c.id]: dim }))
      } else if (c.type === 'position') {
        if (c.dragging && c.position) moving[c.id] = c.position
        else if (c.dragging === false) {
          const pos = c.position ?? dragPos[c.id]
          if (pos) dropped[c.id] = pos
        }
      } else if (c.type === 'select') {
        const { id, selected } = c
        setSel(s => ({ ...s, nodes: toggle(s.nodes, id, selected) }))
      } else if (c.type === 'remove') removed.push(c.id)
    }
    // 여러 개를 같이 끌면 한 번에 여러 노드가 온다
    if (Object.keys(moving).length) {
      setDragPos(p => ({ ...p, ...moving }))
      sync.moving(Object.entries(moving).map(([key, p]): NodeMove => ({ key, x: Math.round(p.x), y: Math.round(p.y) })))
    }
    // 놓을 때 한 번만 도면에 기록 (같이 끈 노드들은 되돌리기 한 번)
    if (Object.keys(dropped).length) {
      setDoc(d => ({ ...d, nodes: d.nodes.map(n => (dropped[n.key] ? { ...n, x: Math.round(dropped[n.key].x), y: Math.round(dropped[n.key].y) } : n)) }))
      setDragPos(p => Object.fromEntries(Object.entries(p).filter(([k]) => !dropped[k])))
    }
    if (removed.length) removeNodes(removed)
  }

  const onEdgesChange = (changes: EdgeChange[]) => {
    const removed: string[] = []
    for (const c of changes) {
      if (c.type === 'select') {
        const { id, selected } = c
        setSel(s => ({ ...s, links: toggle(s.links, id, selected) }))
      } else if (c.type === 'remove') removed.push(c.id)
    }
    if (removed.length) {
      setDoc(d => ({ ...d, links: d.links.filter(l => !removed.includes(l.key)) }))
      setSel(s => ({ ...s, links: s.links.filter(k => !removed.includes(k)) }))
    }
  }

  const removeNodes = (keys: string[]) => {
    const drop = keys.filter(k => doc.nodes.find(n => n.key === k)?.kind !== 'edge')
    if (!drop.length) return
    setDoc(d => ({ ...d, nodes: d.nodes.filter(n => !drop.includes(n.key)), links: d.links.filter(l => !drop.includes(l.from) && !drop.includes(l.to)) }))
    setSel(s => ({ ...s, nodes: s.nodes.filter(k => !drop.includes(k)) }))
  }

  // 고른 것 전부 지우기 (Edge 서버 노드는 남는다)
  const removeSelected = () => {
    const nodeKeys = sel.nodes.filter(k => doc.nodes.find(n => n.key === k)?.kind !== 'edge')
    setDoc(d => ({
      ...d,
      nodes: d.nodes.filter(n => !nodeKeys.includes(n.key)),
      links: d.links.filter(l => !sel.links.includes(l.key) && !nodeKeys.includes(l.from) && !nodeKeys.includes(l.to)),
    }))
    setSel(NONE)
  }

  const onConnect = (c: Connection) => {
    if (!c.source || !c.target || c.source === c.target) return
    const exists = doc.links.some(l => (l.from === c.source && l.to === c.target) || (l.from === c.target && l.to === c.source))
    if (exists) return
    const link = newLink(c.source, c.target, asSide(c.sourceHandle, 'b'), asSide(c.targetHandle, 't'))
    setDoc(d => ({ ...d, links: [...d.links, link] }))
    setSel({ nodes: [], links: [link.key] })
  }

  // 연결선 끝을 끌어 다른 연결점·노드로 옮긴다
  const onReconnect = (old: Edge, c: Connection) => {
    if (!c.source || !c.target || c.source === c.target) return
    setDoc(d => ({
      ...d,
      links: d.links.map(l => (l.key === old.id
        ? { ...l, from: c.source, to: c.target, fromSide: asSide(c.sourceHandle, 'b'), toSide: asSide(c.targetHandle, 't') }
        : l)),
    }))
  }

  // 보이는 화면 가운데
  const centerPos = () => {
    const rect = wrapRef.current?.getBoundingClientRect()
    const p = rect ? rf.screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }) : { x: 0, y: 0 }
    return { x: Math.round((p.x - 90) / 10) * 10, y: Math.round((p.y - 40) / 10) * 10 }
  }

  const addNode = (kind: NetworkNodeKind, patch: Partial<NetworkNode> = {}) => {
    const c = centerPos()
    const pos = freeSpot(doc.nodes, c.x, c.y)
    const node = newNode(kind, pos.x, pos.y, patch)
    setDoc(d => ({ ...d, nodes: [...d.nodes, node] }))
    selectNode(node.key)
    setAddOpen(false)
  }

  const focusNode = (key: string) => {
    const n = doc.nodes.find(x => x.key === key)
    if (!n) return
    selectNode(key)
    rf.setCenter(n.x + 95, n.y + 40, { zoom: Math.max(rf.getZoom(), 1), duration: 300 })
  }

  // Ctrl+Z / Ctrl+Y — 입력칸 밖에서만. 되돌리기는 내가 바꾼 항목만
  const keyRef = useRef(sync)
  useEffect(() => { keyRef.current = sync })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('input, textarea, select, [contenteditable]')) return
      if (!(e.ctrlKey || e.metaKey)) return
      const k = e.key.toLowerCase()
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); keyRef.current.undo() }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); keyRef.current.redo() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // 전체 화면 — F 로 켜고 끄기, Esc 로 닫기 (입력칸에서 누른 키는 무시)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) return
      if (e.key === 'Escape') setFull(false)
      else if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        setFull(f => !f)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // 자동 정렬 — 한 번의 변경이라 Ctrl+Z 한 번에 되돌아간다
  const arrange = () => {
    setDoc(autoLayout)
    setTimeout(() => rf.fitView({ padding: 0.2, duration: 300 }), 50)
  }
  useEffect(() => {
    const t = setTimeout(() => rf.fitView({ padding: 0.2, duration: 200 }), 60)
    return () => clearTimeout(t)
  }, [full, rf])

  // 지워진 항목(남이 지운 것 포함)은 빼고 센다
  const selNodes = doc.nodes.filter(n => sel.nodes.includes(n.key))
  const selLinks = doc.links.filter(l => sel.links.includes(l.key))
  const selNode = selNodes.length === 1 && selLinks.length === 0 ? selNodes[0] : undefined
  const selLink = selLinks.length === 1 && selNodes.length === 0 ? selLinks[0] : undefined
  const multi = selNodes.length + selLinks.length > 1
  const patchNode = (key: string, patch: Partial<NetworkNode>) =>
    setDoc(d => ({ ...d, nodes: d.nodes.map(n => (n.key === key ? { ...n, ...patch } : n)) }))
  const statusTone = sync.status === 'live' ? 'text-gray-400' : sync.status === 'error' ? 'text-red-600' : 'text-amber-600'
  const btn = 'border border-gray-300 rounded-md px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40'

  return (
    <div className={full ? 'fixed inset-0 z-50 flex flex-col bg-surface'
      : 'h-full min-h-[520px] flex flex-col bg-surface rounded-xl border border-gray-200 overflow-hidden'}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100 shrink-0">
        <div className="relative">
          <button onClick={() => setAddOpen(o => !o)} className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-xs hover:bg-accent-hover">+ 장비 추가</button>
          {addOpen && (
            <div className="absolute left-0 top-full mt-1 z-20 bg-surface border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px]"
              onMouseLeave={() => setAddOpen(false)}>
              {ADDABLE.map(k => (
                <button key={k.kind} onClick={() => addNode(k.kind)} className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100">{k.label}</button>
              ))}
            </div>
          )}
        </div>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <button onClick={sync.undo} disabled={!sync.canUndo} className={btn} title="되돌리기 (Ctrl+Z)">↶</button>
        <button onClick={sync.redo} disabled={!sync.canRedo} className={btn} title="다시 실행 (Ctrl+Y)">↷</button>
        <button onClick={arrange} disabled={doc.nodes.length < 2} className={btn}
          title="자동 정렬">자동 정렬</button>
        <button onClick={() => rf.fitView({ padding: 0.2, duration: 300 })} className={btn}>전체 보기</button>
        <button onClick={() => setFull(f => !f)} className={btn} title={full ? '전체 화면 닫기 (F, Esc)' : '전체 화면 (F)'}>
          {full ? '전체 화면 닫기' : '전체 화면'}
        </button>
        {edgeNote && <span className="text-[11px] text-amber-600">{edgeNote}</span>}
        <div className="ml-auto flex items-center gap-3">
          {/* 지금 이 도면을 보고 있는 사람 (나 포함) */}
          <div className="flex -space-x-1.5">
            {sync.viewers.map(v => (
              <span key={v.connectionId} title={`${v.name}${v.connectionId === sync.me ? ' (나)' : ''}`}
                className="w-6 h-6 rounded-full border-2 border-surface text-[10px] font-semibold text-white flex items-center justify-center"
                style={{ background: VIEWER_COLORS[v.color % VIEWER_COLORS.length] }}>
                {v.name.slice(0, 1)}
              </span>
            ))}
          </div>
          <span className={`text-xs whitespace-nowrap ${sync.error ? 'text-red-600' : statusTone}`} title={sync.error ?? undefined}>
            {sync.error ?? STATUS_TEXT[sync.status]}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex">
        <div ref={wrapRef} className="flex-1 min-w-0 relative">
          <ReactFlow<NetFlowNode, Edge>
            className="net-flow"
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            connectionRadius={24}
            onPaneClick={() => setSel(NONE)}
            onPaneContextMenu={e => e.preventDefault()}
            // 왼쪽 드래그 = 상자로 고르기 (걸치기만 해도), 가운데·오른쪽 드래그나 Space+드래그 = 화면 이동
            selectionOnDrag
            selectionMode={SelectionMode.Partial}
            panOnDrag={[1, 2]}
            multiSelectionKeyCode={['Shift', 'Control', 'Meta']}
            connectionMode={ConnectionMode.Loose}
            deleteKeyCode={['Delete', 'Backspace']}
            defaultEdgeOptions={{ markerEnd: { type: MarkerType.Arrow, width: 0, height: 0 } }}
            colorMode={dark ? 'dark' : 'light'}
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1.2 }}
            minZoom={0.2}
            snapToGrid
            snapGrid={[10, 10]}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} />
            <Controls showInteractive={false} />
          </ReactFlow>
          {!sync.ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface/70 text-xs text-gray-500 z-10">
              {sync.status === 'error' ? sync.error : '도면에 연결하는 중…'}
            </div>
          )}
          {sync.ready && doc.nodes.length <= 1 && (
            <div className="absolute inset-x-0 bottom-6 text-center text-xs text-gray-400 pointer-events-none">
              [+ 장비 추가]로 IP가 있는 장비를 놓고 테두리 점을 끌어 연결하세요.
            </div>
          )}
        </div>

        <aside className="w-80 shrink-0 border-l border-gray-100 overflow-y-auto">
          {selNode ? (
            <NodeForm key={selNode.key} node={selNode} comms={commsOf(selNode, live)}
              onChange={p => patchNode(selNode.key, p)}
              onDelete={selNode.kind === 'edge' ? null : () => removeNodes([selNode.key])} />
          ) : selLink ? (
            <LinkForm key={selLink.key} link={selLink} from={nameOf(selLink.from)} to={nameOf(selLink.to)}
              onChange={p => setDoc(d => ({ ...d, links: d.links.map(l => (l.key === selLink.key ? { ...l, ...p } : l)) }))}
              onDelete={() => { setDoc(d => ({ ...d, links: d.links.filter(l => l.key !== selLink.key) })); setSel(NONE) }} />
          ) : multi ? (
            <MultiForm nodes={selNodes.map(n => nodeView(n, live).name || kindInfo(n.kind).label)} links={selLinks.length}
              locked={selNodes.some(n => n.kind === 'edge')} onDelete={removeSelected} onClear={() => setSel(NONE)} />
          ) : (
            <Overview doc={doc} rows={rows} orphans={orphans} onUpdate={setDoc} onAddAt={(kind, ip) => addNode(kind, { ip })} onFocus={focusNode} />
          )}
          <div className="px-4 py-3 text-[11px] text-gray-400 border-t border-gray-100 space-y-0.5">
            <div>● 코드: 그 IP로 통신하는 디바이스 (램프 = 통신 상태)</div>
            <div>선택: 드래그, Shift·Ctrl+클릭 · 삭제: Delete</div>
            <div>화면 이동: 우클릭 드래그, Space+드래그 · 전체 화면: F</div>
            <div>EM에만 저장되며 Edge 설정은 변경되지 않습니다.</div>
          </div>
        </aside>
      </div>
    </div>
  )
}

